(ns web3.components.drawer
  (:require [reagent.core :as r]
            [goog.dom.classes :as classes]
            [web3.view :as ui-util]
            [re-frame-simple.db :as db]
            [web3.constants :as constants]
            [web3.utils :as utils]
            [web3.ui-state :as state]
            [web3.ui-state :as ui-state]
            [web3.view :as v]
            [web3.icons.shapes :as shapes]))

(def frame :div.width-100p)

(defn current-frame [{:keys [state props] :as component}]
  (let [frames (:frames props)
        back! (:back! @state)]
    (-> (dec (count frames))
        (- (or back! 0)))))

(defn handle-frame-transition! [{:keys [state props] :as component}]
  (let [{:keys [back!]} @state
        {:keys [on-back]} props]
    (when back!
      (js/setTimeout (partial on-back back!) 200))))

(defn header [{:keys [title
                      after
                      content-right
                      last-frame?]}]
  (let [pad-right (empty? content-right)]
    [:div.bg-white
     {:key "drawer-header"}
     [:div.flex.items-center.relative.flex-none.margin-bottom-3
      {:style {:box-shadow "0 0 1em #8F9296"
               :z-index 1}}
      [:div.drawer__action_back.pad-v-3.pad-h-3.cursor-pointer.hover-text-dark-gray.flex.items-center
       (if last-frame?
         shapes/close-x
         shapes/chevron-left)]
      [:div.flex-auto.text-center.text-uppercase.pad-h-3.text-size-14
       title]
      (when pad-right [:div.flex-none {:style {:width 41}}])
      content-right]
     after]))

(defn body [props & content]
  (let [[props content] (if (map? props) [props content] [nil (cons props (seq content))])]
    (into [:div.bg-off-white.flex.flex-column.relative
           (assoc props :key "drawer-body")]
          content)))

(defn bottom-button [{:keys [color href] :as props
                      :or {color :black}} & content]
  [:div.pad-all-3
   (into [(if href :a :div)
          (-> props
              (dissoc :color)
              (cond-> (#{:off-white :white} color) (-> (assoc-in [:style :border] "1px solid #e2e2e2")
                                                       (update :class str " hover-bg-white ")))
              (update :class str
                      " round-3 block text-center text-uppercase text-size-14 pad-all-2_5 cursor-pointer relative"
                      (case color :black " bg-black text-white hover-bg-black-hover " nil)))]
         content)])

(defn drawer-frame
  "Display a single drawer frame.

  Child elements with the class `drawer__action_back`, when clicked, will drop the current frame."
  [{:keys [on-back content route-params active? last-frame?] :as props}]
  [:div.flex-none.relative.overflow-y-auto.bg-off-white
   (-> props
       (dissoc :on-back :content :route-params :last-frame? :active?)
       (assoc :on-click (fn [e]
                          (when (utils/closest
                                  (.-target e)
                                  #(classes/has % "drawer__action_back"))
                            (on-back))))
       (update :style merge {:width "20%"
                             :overflow-x "hidden"
                             (if (and active? (ui-state/mobile?))
                               :min-height
                               :height) "100vh"}))
   [header (assoc (some-> content
                          (v/class-get :header)
                          (utils/eval-if-fn))
             :last-frame? last-frame?)]
   (when content
     [content route-params])])

(def temporary-drawer
  "Temporary drawer with support for displaying a stack of frames.

  Expects a vector of :frames and an :on-back callback.

  :on-back should be a function of the form:
  (fn [num-frames] ...remove n frames from end of `frames` vector...)

  The last frame will be displayed. If more than one frame exists, a
  back button will be displayed at the top of the frame."
  (r/create-class
    {:display-name
     "Drawer"

     :component-will-receive-props
     (fn [{:as this
           :keys [state props]} [_ next-props]]
       (let [opened? (boolean (and (not (seq (:frames props)))
                                   (seq (:frames next-props))))]

         (ui-util/swap-silently! state
                                 (fn [st]
                                   (cond-> (dissoc st :back!)
                                           opened? (assoc :start-handler (-> (:frames next-props)
                                                                             (last)
                                                                             (:handler)))))
                                 :back! nil))
       #_((:will-receive-props ui-state/manage-scroll) :router/drawer this next-argv))

     :component-did-update
     (fn [{:keys [props] :as this}]
       (handle-frame-transition! this)
       #_((:did-update ui-state/manage-scroll) :router/drawer this))

     :reagent-render
     (fn [{:keys [on-back
                  frames
                  active?]}]
       (let [{:keys [state] :as this} (r/current-component)
             {:keys [back! start-handler]} @state
             current-frame (current-frame this)
             start-frame? (and (seq frames)
                               (= (:handler (get frames current-frame))
                                  start-handler))
             drawer-transform (str "translateX(" (if (neg? current-frame) "100%" "0%") ")")
             mobile? (ui-state/mobile?)]
         [:div#drawer.modal-container
          {:class (if (neg? current-frame) "modal--bg-off" "modal--bg")
           :on-click (fn [^js e]
                       (when (= (.-target e)
                                (.-currentTarget e))
                         (swap! state assoc :back! 999)))
           :style (merge
                    {:left 0
                     :right 0
                     :overflow-x "hidden"
                     :position (if mobile? "absolute"
                                           "fixed")}
                    (when (seq frames)
                      {:min-height "100%"
                       :z-index (:router/drawer constants/z-index)})
                    (when-not active?
                      (:inactive-styles ui-state/manage-scroll)))}
          [:div.drawer__tray.shadow-drawer.modal-content
           {:style (merge {:z-index (inc (:router/drawer constants/z-index))
                           :transform drawer-transform}
                          (if mobile?
                            {:left 0
                             :right 0
                             :margin-left 0}
                            {:max-width (str (min 475
                                                  (cond-> (:window/inner-width @state/ui-globals)
                                                          (< current-frame 1)
                                                          (- 65))) "px")}))}
           #_(when-not mobile?
               [:div.absolute.cursor-pointer.dark.text-white
                {:on-click #(swap! state assoc :back! 999)
                 :style (merge {:width 50
                                :opacity (if (neg? current-frame) 0 1)
                                :transition "0.15s ease opacity"}
                               (if mobile?
                                 {:padding 10
                                  :z-index 100}
                                 {:text-align "right"
                                  :left -50
                                  :top 25}))}

                widgets/close-icon])
           [:div.height-100p.width-100p.relative.overflow-hidden
            (into [:div.height-100p.flex.text-size-15.weight-medium.line-h-19
                   {:ref #(db/assoc-in! [:refs :ref/drawer] %)
                    :style {:width "500%"
                            :transition "0.15s ease transform"
                            :transform (str "translateX(" (- (* (max 0 current-frame)
                                                                20)) "%)")}}]

                  (->> (concat frames (repeat {}))
                       (take 5)
                       (map-indexed (fn [i {:keys [handler route-params path view]}]
                                      [drawer-frame
                                       (-> {:content view
                                            :route-params route-params
                                            :key handler
                                            :active? (= i current-frame)
                                            :last-frame? (= 0 i)}
                                           (assoc
                                             :on-back #(swap! state assoc :back!
                                                              (if start-frame? 999 1))))]))))]]]))}))

(def drawer-list-spacer
  [:div.bg-light-gray.margin-h-3.flex-none
   {:style {:height 1}}])
